/**
 * V3 — Cockpit Groupe : KPIs consolidés multi-entités + alertes équipe.
 */
import React, { useEffect, useState } from 'react';
import { api } from './api';
import { Banknote, Building2, TrendingUp, TrendingDown, AlertCircle, Activity, FileText, Fuel } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, ComposedChart, Line, Legend } from 'recharts';

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');
const ENTITY_COLORS = {
  CIS: '#22c55e', ROPS: '#f59e0b', NETBTP: '#ef4444',
  NKEYI: '#8b5cf6', R2S: '#fb923c', EXCUISINE: '#6366f1',
};

function fmtAgo(ms) {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}j`;
}

function shortUser(u) {
  if (!u) return '?';
  const cleaned = u.replace(/^(RECOVERY_|MERGE_|DEDUPE_|RESTORE_)/, '');
  return cleaned.split('.').pop();
}

export default function V3Groupe() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.resolve().then(async () => {
      try {
        const { data: d } = await api.get('/api/v3/groupe/cockpit');
        setData(d);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    });
  }, []);

  if (err) return <div className="v3-error">⚠️ {err}</div>;
  if (!data) return <div className="v3-skeleton">Chargement du cockpit…</div>;

  const k = data.kpis_groupe;
  const topEntities = data.top_entities.slice(0, 6);

  return (
    <div className="v3-page" data-testid="v3-groupe">
      <div className="v3-page-head">
        <h1>Cockpit Groupe</h1>
        <p>Vue consolidée toutes entités · ROPS · CIS · NETBTP · NKEYI · R2S · EXCUISINE</p>
      </div>

      {/* Alertes */}
      {data.alerts.length > 0 && (
        <section className="v3-alerts">
          {data.alerts.slice(0, 3).map((a, i) => (
            <div key={i} className="v3-alert">
              <AlertCircle size={16} />
              <span>{a.message}</span>
            </div>
          ))}
        </section>
      )}

      {/* KPI Groupe */}
      <section className="v3-kpi-grid">
        <KpiCard icon={<TrendingUp/>} label="Entrées caisse" value={fmt(k.caisse_total_entrees) + ' FCFA'}
                 sub={`${k.caisse_operations} opérations`} accent="emerald" />
        <KpiCard icon={<TrendingDown/>} label="Sorties caisse" value={fmt(k.caisse_total_sorties) + ' FCFA'}
                 sub="Toutes entités" accent="rose" />
        <KpiCard icon={<Banknote/>} label="Solde caisse" value={fmt(k.caisse_solde_net) + ' FCFA'}
                 sub="Net groupe" accent={k.caisse_solde_net >= 0 ? 'emerald' : 'rose'} />
        <KpiCard icon={<Fuel/>} label="CA ROPS 30j" value={fmt(k.rops_ca_30j) + ' FCFA'}
                 sub={`${k.rops_days_saisis} jours saisis`} accent="amber" />
        <KpiCard icon={<FileText/>} label="Écritures Sage" value={k.sage_ecritures}
                 sub={`Débit: ${fmt(k.sage_total_debit)}`} accent="cyan" />
        <KpiCard icon={<Building2/>} label="Entités actives" value={topEntities.length}
                 sub={`sur ${6} entités du groupe`} accent="violet" />
      </section>

      <section className="v3-grid-2">
        <div className="v3-card v3-card-chart">
          <div className="v3-card-head">
            <h2>Performance caisse par entité</h2>
            <span className="v3-card-sub">Entrées vs Sorties</span>
          </div>
          <div className="v3-card-body" style={{ height: 280 }}>
            {topEntities.length === 0 ? (
              <div className="v3-empty">Aucune donnée caisse pour l&apos;instant.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topEntities}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                  <XAxis dataKey="entity" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => fmt(v)} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                    formatter={(v) => [fmt(v) + ' FCFA']}
                  />
                  <Bar dataKey="entrees" name="Entrées" radius={[3, 3, 0, 0]}>
                    {topEntities.map((e, i) => <Cell key={i} fill={ENTITY_COLORS[e.entity] || '#94a3b8'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="v3-card">
          <div className="v3-card-head">
            <h2>Activité récente équipe</h2>
            <span className="v3-card-sub">Dernières 48h</span>
          </div>
          <div className="v3-card-body">
            {data.recent_activity.length === 0 && <div className="v3-empty">Aucune activité récente</div>}
            {data.recent_activity.slice(0, 8).map((r, i) => (
              <div key={i} className="v3-activity-item">
                <Activity size={14} className="v3-activity-icon" />
                <div className="v3-activity-text">
                  <div className="v3-activity-user">{shortUser(r.user)}</div>
                  <div className="v3-activity-key">{(r.key || '').replace(/^groupe_/, '').replace(/_/g, ' ')}</div>
                </div>
                <span className="v3-activity-time">{fmtAgo(r.ts)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CA Groupe — vue 6 mois (toujours affiché) */}
      {data.monthly_series && (
        <section className="v3-card" style={{ marginBottom: 22 }} data-testid="v3-monthly-chart">
          <div className="v3-card-head">
            <h2>Évolution CA Groupe — 6 derniers mois</h2>
            <span className="v3-card-sub">Caisse Entrées · ROPS Piste · ROPS Shop · Litres carburant</span>
          </div>
          <div className="v3-card-body" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.monthly_series}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => fmt(v)} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => fmt(v) + ' L'} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }}
                  formatter={(v, name) => {
                    if (name === 'Litres total') return [fmt(v) + ' L', name];
                    return [fmt(v) + ' FCFA', name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                <Bar yAxisId="left" dataKey="caisse_entrees" name="Caisse Entrées" fill="#10b981" radius={[3,3,0,0]} />
                <Bar yAxisId="left" dataKey="rops_ca_piste" name="ROPS Piste" fill="#f59e0b" radius={[3,3,0,0]} />
                <Bar yAxisId="left" dataKey="rops_ca_shop" name="ROPS Shop" fill="#8b5cf6" radius={[3,3,0,0]} />
                <Line yAxisId="right" type="monotone" dataKey="rops_litres_total" name="Litres total" stroke="#06b6d4" strokeWidth={2.5} dot={{ r: 3, fill: '#06b6d4' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      <section className="v3-card">
        <div className="v3-card-head">
          <h2>Détail par entité</h2>
          <span className="v3-card-sub">Caisse & Comptabilité</span>
        </div>
        <div className="v3-card-body">
          <div className="v3-entity-grid">
            {Object.entries(data.by_entity).map(([ent, info]) => (
              <div key={ent} className="v3-entity-card"
                   style={{ borderTopColor: ENTITY_COLORS[ent] || '#94a3b8' }}>
                <div className="v3-entity-name" style={{ color: ENTITY_COLORS[ent] || '#94a3b8' }}>{ent}</div>
                <div className="v3-entity-stat">
                  <span>Solde caisse</span>
                  <strong className={info.solde >= 0 ? 'pos' : 'neg'}>{fmt(info.solde)}</strong>
                </div>
                <div className="v3-entity-stat">
                  <span>Opérations</span>
                  <strong>{info.operations}</strong>
                </div>
                <div className="v3-entity-stat">
                  <span>Écritures Sage</span>
                  <strong>{data.sage_by_entity[ent]?.ecritures || 0}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TopClientsSection />
      <ExpiringDevisSection />
    </div>
  );
}

function ExpiringDevisSection() {
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({ count: 0, total_ttc_at_risk: 0, urgent_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.resolve().then(async () => {
      try {
        const { data } = await api.get('/api/v3/devis/expiring-soon', { params: { days: 7 } });
        setItems(data.items || []);
        setKpis(data.kpis || {});
      } catch (e) {
        console.error('Expiring devis err:', e?.message);
      } finally {
        setLoading(false);
      }
    });
  }, []);

  return (
    <section className="v3-card" data-testid="cockpit-expiring-devis" style={{ marginTop: 24 }}>
      <div className="v3-card-head">
        <h3>Devis qui expirent dans 7 jours</h3>
        <span className="v3-card-sub">
          {kpis.count} devis · {fmt(kpis.total_ttc_at_risk)} FCFA à risque
          {kpis.urgent_count > 0 && (
            <span style={{ color: '#fca5a5', marginLeft: 8 }}>
              · {kpis.urgent_count} urgent{kpis.urgent_count > 1 ? 's' : ''} (≤ 2j)
            </span>
          )}
        </span>
      </div>
      {loading ? (
        <div className="v3-empty">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="v3-empty">Aucun devis n&apos;expire dans les 7 prochains jours.</div>
      ) : (
        <div className="v3-table-wrap">
          <table className="v3-table">
            <thead>
              <tr>
                <th>Échéance</th>
                <th>Reste</th>
                <th>Entité</th>
                <th>N°</th>
                <th>Client</th>
                <th>Statut</th>
                <th className="num">TTC</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`expiring-devis-${it.id}`}>
                  <td className="mono strong">{it.echeance}</td>
                  <td>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 4,
                      background: (it.days_left <= 2) ? 'rgba(239,68,68,0.15)' : 'rgba(251,191,36,0.15)',
                      color: (it.days_left <= 2) ? '#fca5a5' : '#fcd34d',
                      fontWeight: 700,
                    }}>
                      J+{it.days_left ?? '?'}
                    </span>
                  </td>
                  <td><span className="v3-entity-pill" style={{
                    background: (ENTITY_COLORS[it.entity] || '#94a3b8') + '20',
                    color: ENTITY_COLORS[it.entity] || '#94a3b8',
                  }}>{it.entity}</span></td>
                  <td className="mono">{it.numero}</td>
                  <td>{it.client}</td>
                  <td><span className="v3-pill v3-pill-cat">{it.statut}</span></td>
                  <td className="num strong" style={{ color: '#d6f32f' }}>{fmt(it.montant_ttc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TopClientsSection() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total_clients_distincts: 0, ca_total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.resolve().then(async () => {
      try {
        const { data } = await api.get('/api/v3/groupe/top-clients', { params: { limit: 10 } });
        setItems(data.items || []);
        setMeta({
          total_clients_distincts: data.total_clients_distincts || 0,
          ca_total: data.ca_total || 0,
        });
      } catch (e) {
        console.error('Top clients err:', e?.message);
      } finally {
        setLoading(false);
      }
    });
  }, []);

  return (
    <section className="v3-card" data-testid="cockpit-top-clients" style={{ marginTop: 24 }}>
      <div className="v3-card-head">
        <h3>Top 10 clients · CA cumulé Groupe</h3>
        <span className="v3-card-sub">{meta.total_clients_distincts} clients · CA total {fmt(meta.ca_total)} FCFA</span>
      </div>
      {loading ? (
        <div className="v3-empty">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="v3-empty">Aucun client encore.</div>
      ) : (
        <div className="v3-table-wrap">
          <table className="v3-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Client</th>
                <th>Entités</th>
                <th className="num">Documents</th>
                <th className="num">Dernier</th>
                <th className="num">CA TTC</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.rank} data-testid={`top-client-row-${c.rank}`}>
                  <td className="mono strong">#{c.rank}</td>
                  <td>{c.client}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {(c.entities || []).map((e) => (
                      <span key={e} className="v3-entity-pill" style={{
                        background: (ENTITY_COLORS[e] || '#94a3b8') + '20',
                        color: ENTITY_COLORS[e] || '#94a3b8',
                        marginRight: 3, fontSize: 9, padding: '1px 5px',
                      }}>{e}</span>
                    ))}
                  </td>
                  <td className="num">
                    <span title={`Factures: ${c.nb_factures} · BL: ${c.nb_bls}`}>
                      {c.nb_documents}
                    </span>
                  </td>
                  <td className="num mono">{c.last_date || '—'}</td>
                  <td className="num strong" style={{ color: '#d6f32f' }}>{fmt(c.total_ttc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function KpiCard({ icon, label, value, sub, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
        {sub && <div className="v3-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}
